/**
 * Watcher do convite de avaliação — irmão do `PushPermissionWatcher`, montado
 * no mesmo lugar (`AppFrame`, porque a folha desenha `fixed` cru sem portal).
 *
 * Duas responsabilidades, e só elas:
 *
 *  1. **Registrar o dia de uso** (uma vez por abertura, só no app nativo) —
 *     é o contador que o "certo tempo de uso" lê.
 *  2. **Elegibilidade**: logado + onboarding concluído + app das lojas + **o
 *     convite de push já resolvido**. A última é a regra de prioridade da
 *     casa: o app nunca faz duas perguntas na mesma vida útil de um convite —
 *     enquanto o push ainda PODE ser oferecido neste aparelho, a avaliação
 *     espera. Push é one-shot e vale mais; avaliação re-oferece sem custo.
 *     (Também elimina por construção a corrida de dois sheets abrindo no
 *     mesmo assentamento — nenhum gate compartilhado a arbitrar.)
 *
 * Todo o "quando" (rota segura, celebrationGate, assentamento, snooze/teto)
 * vive em `useReviewPromptGate` + `canOfferReviewPrompt`.
 */

import { lazy, Suspense, useEffect } from "react";
import { useAuth } from "../../../hooks/useAuth";
import { isNativeApp } from "../../../lib/platform/native";
import { canOfferPushPrompt } from "../../notifications/utils/pushPrompt";
import { useReviewPromptGate } from "../hooks/useReviewPromptGate";
import { recordReviewUsageDay } from "../utils/reviewPrompt";

// `lazy` pela mesma razão do PushPermissionWatcher, com outro peso no fim do
// fio: a folha importa `reviewStoreLink` → `appStores` → o módulo de conteúdo
// público inteiro, que não pode entrar no chunk de entrada (queixa de bundle
// medida em 03/08). Quem nunca vê o convite — toda sessão web, e a maioria
// das nativas — não paga a prosa.
const ReviewPromptSheet = lazy(() =>
  import("./ReviewPromptSheet").then((m) => ({ default: m.ReviewPromptSheet })),
);

export function ReviewPromptWatcher() {
  const { user, profile } = useAuth();

  // Dia de uso conta por ABERTURA do app nativo, logado — antes disso não há
  // instalação em uso de verdade. O módulo deduplica o mesmo dia sozinho.
  const isNative = isNativeApp();
  useEffect(() => {
    if (isNative && user) recordReviewUsageDay();
  }, [isNative, user]);

  const hasRegisteredToken = (profile?.fcmTokens?.length ?? 0) > 0;
  const pushStillPending = canOfferPushPrompt({ hasRegisteredToken });

  const eligible =
    !!user && profile?.onboardingCompleted === true && isNative && !pushStillPending;

  const { open, close } = useReviewPromptGate(eligible);

  if (!open) return null;
  return (
    <Suspense fallback={null}>
      <ReviewPromptSheet onClose={close} />
    </Suspense>
  );
}
